import {
    action,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import { User } from "oidc-client-ts";
import RootStore, { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import type { NavigateFunction } from "react-router-dom";
import { DashboardDomain } from "../../../../models/Dashboard/DashboardDefinition";
import {
    ReportDataField,
    ReportDataFieldVariation,
    ReportFieldDataTypes,
} from "../../../../models/Reporting/ReportDataField";
import {
    FieldUse,
    ReportField,
} from "../../../../models/Reporting/ReportField";
import {
    Comparator,
    ReportFilter,
} from "../../../../models/Reporting/ReportFilter";
import { AuthStore } from "../../../../stores/AuthStore";
import { BaseStore } from "../../../../stores/BaseStore";
import { DateReferenceOption } from "../../../../stores/ComponentStores/DatePickerComponentStore";
import {
    DashboardStore,
    IDashboardItem,
} from "../../../../stores/Layout/DashboardStore";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { agentDashboardTabNames, AgentDashboardTabs } from "../AgentDashboard";
import type { Moment } from "moment";
import {
    parseFromISO,
    serializeToUtc,
    toIsoByDateReference,
} from "utils/DateTimeUtils";
import _ from "lodash";
import { isNullableType } from "utils/TypeGuards";
import * as AgentCoachingService from "services/AgentCoachingService";
import {
    expandCompletedCoachingReviewTargets,
    matchScoreToEval,
} from "../Views/EvalCoachingTable/EvalCoachingTableViewModel";
import {
    AgentCoaching,
    AgentCoachingStatus,
    targetedConversationIds,
} from "models/AgentModels/AgentCoaching";
import {
    CoachingLockStatus,
    EvalReviewStore,
} from "components/Agents/EvalReview/Stores/EvalReviewStore";
import { AgentCoachingStore } from "components/Agents/Coaching/Stores/AgentCoachingStore";
import { WidgetContentType, WidgetType } from "models/Dashboard/Widget";
import { DynamicTimeSpan } from "utils/reportingTimeHelpers";
import { setAgentDashboardDataGridColumns } from "./Helpers";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { InteractionType } from "models/InteractionType";
import { Routes } from "components/Navigation/Routes";

export const CoachSelectedEvaluationsTask = "Coach Selected Evaluations";

/**
 * Standalone coaching page, used whenever coaching spans more than one target and
 * so cannot be hosted inside a single conversation.
 */
export const EvaluationReviewRoute = "/app/agentcoaching/evaluationreview";

export const agentDashboardDataGridTabs = Object.keys(
    AgentDashboardTabs,
).filter(
    (key) => isNaN(Number(key)) && key !== "Dashboard",
) as (keyof typeof AgentDashboardTabs)[];

@AcxStore
export class AgentDashboardStore extends BaseStore {
    dashboardStore?: DashboardStore;
    private readonly agentCoachingService: AgentCoachingService.AgentCoachingService =
        new AgentCoachingService.AgentCoachingService();
    authStore: AuthStore;
    agentCoachingStore: AgentCoachingStore;

    @observable.ref
    agentEvaluations?: AgentCoachingService.AgentEvaluations;

    @observable private user?: User | null;
    @observable orgId?: string;
    @observable agentId?: string;
    @observable agentName: string;
    @observable showMachineEvals: boolean = false;
    @observable dgStoreMap: Record<
        keyof typeof AgentDashboardTabs,
        AcxDataGridStore
    > = {} as Record<keyof typeof AgentDashboardTabs, AcxDataGridStore>;

    @observable agentDrafts: AgentCoaching[] = [];

    @observable tabsByPerm: string[] = [];

    @observable currentTab: number = 0;

    @observable confirmDeleteIsOpen: boolean = false;

    @observable coachingDetails: AgentCoachingService.AgentCoachingDetails[] =
        [];

    constructor(private rootStore: IRootStore) {
        super("Agent Dashboard");

        makeObservable(this);

        this.authStore = rootStore.getStore(AuthStore);
        this.agentCoachingStore = rootStore.getStore(AgentCoachingStore);

        this.tabsByPerm = agentDashboardTabNames.filter((tabName) => {
            return this.canUserViewTab(tabName);
        });

        setAgentDashboardDataGridColumns(this);

        rootStore
            .getStore(AuthStore)
            .getUserObject()
            .then((value) => {
                runInAction(() => {
                    this.user = value;
                });
            })
            .catch((reason) =>
                console.error(`Failed to retrieve User Profile ${reason}`),
            );

        reaction(
            (r) => ({
                orgId: this.orgId,
                user: this.user,
                agentId: this.agentId,
                agentName: this.agentName,
                activeLocation: this.rootStore.activeLocation,
                currentTab: this.currentTab,
            }),
            (arg) => {
                if (
                    (arg.activeLocation &&
                        !arg.activeLocation.location.includes(
                            "/app/agentcoaching/dashboard/",
                        )) ||
                    arg.currentTab !==
                        this.tabsByPerm.findIndex((i) => i === "Dashboard")
                ) {
                    return;
                }

                this.dashboardStore = new DashboardStore(
                    rootStore,
                    DashboardDomain.Agent,
                );

                if (arg.orgId && arg.user && arg.agentId && arg.agentName) {
                    this.dashboardStore.initializeStoreParameters(
                        arg.orgId,
                        arg.user,
                        // this.buildImmutableWidgets(arg.agentId, arg.orgId), // TODO: review this, we should probably just remove these widgets from the db
                        [],
                        this.buildDashboardDynamicFilters(
                            arg.agentName,
                            arg.agentId,
                            arg.orgId,
                        ),
                        true,
                    );
                }
            },
            { fireImmediately: true, delay: 0 },
        );
    }

    private buildDashboardDynamicFilters(
        agentName: string,
        agentId: string,
        orgId: string,
    ) {
        const filterMap = new Map<string, ReportFilter>();

        const agentNameFilter = new ReportFilter();
        agentNameFilter.value = decodeURIComponent(agentName);
        agentNameFilter.comparator = Comparator.EQ;
        agentNameFilter.reportField = new ReportField(
            undefined,
            undefined,
            undefined,
            undefined,
        );
        agentNameFilter.reportField.fieldUse = FieldUse.QuickFilter;
        agentNameFilter.reportField.reportDataField = new ReportDataField();
        agentNameFilter.reportField.reportDataField.variation =
            ReportDataFieldVariation.Concrete;
        agentNameFilter.reportField.reportDataField.datatype =
            ReportFieldDataTypes.Text;
        agentNameFilter.reportField.reportDataField.fieldName = "AgentName";
        agentNameFilter.reportField.reportDataField.value = "AgentName";

        filterMap.set("AgentName", agentNameFilter);

        const agentIdFilter = new ReportFilter();
        agentIdFilter.value = agentId;
        agentIdFilter.comparator = Comparator.EQ;
        agentIdFilter.reportField = new ReportField(
            undefined,
            undefined,
            undefined,
            undefined,
        );
        agentIdFilter.reportField.fieldUse = FieldUse.QuickFilter;
        agentIdFilter.reportField.reportDataField = new ReportDataField();
        agentIdFilter.reportField.reportDataField.variation =
            ReportDataFieldVariation.Concrete;
        agentIdFilter.reportField.reportDataField.datatype =
            ReportFieldDataTypes.Guid;
        agentIdFilter.reportField.reportDataField.fieldName = "AgentId";
        agentIdFilter.reportField.reportDataField.value = "AgentId";

        filterMap.set("AgentId", agentIdFilter);

        return filterMap;
    }

    private buildImmutableWidgets(
        agentId: string,
        orgId: string,
    ): Array<IDashboardItem> {
        const key = "eval-coaching-table";
        return [
            {
                key: key,
                widgetType: WidgetType.EvaluationCoachingTable,
                name: "Evaluations",
                args: { isTable: true, agentId: agentId, orgId: orgId },
                organizationId: orgId,
                layout: {
                    i: key,
                    x: 0,
                    y: 0,
                    static: true,
                    w: 12,
                    h: 4,
                    minH: 4,
                },
                contentType: WidgetContentType.IMMUTABLE,
                dateReference: DateReferenceOption.EvaluationCompleteDate,
                timeSpan: DynamicTimeSpan.TENYEAR,
            },
        ];
    }

    @action
    setCurrentTab(val: number) {
        this.currentTab = val;
    }

    @action
    setAgentAndOrg(orgId: string, agentId: string, agentName: string) {
        this.orgId = orgId;
        this.agentId = agentId;
        this.agentName = agentName;
    }

    @action
    async loadAgentEvaluations(
        orgId: string,
        agentId: string,
        startDate: Moment,
        endDate: Moment,
        dateReference?: DateReferenceOption,
        hierarchyIds?: string[],
    ) {
        // set all loading to true so that we dont see an empty dataGrid prematurely for MyDrafts
        agentDashboardDataGridTabs.forEach((key) => {
            this.dgStoreMap[key].isLoading = true;
        });
        const res = await this.agentCoachingService.getAgentEvaluations(
            agentId,
            serializeToUtc(startDate)!,
            serializeToUtc(endDate)!,
            dateReference,
            this.authStore.isLoggedInUserAgent(),
            hierarchyIds,
            this.agentCoachingStore.showMachineEvals,
        );

        runInAction(async () => {
            res.all.sort((a, b) => {
                if (
                    (_.get(b, "interaction.interactionDate") ?? 0) <
                        (_.get(a, "interaction.interactionDate") ?? 0) ||
                    isNullableType(_.get(b, "interaction.interactionDate"))
                ) {
                    return -1;
                }
                if (
                    (_.get(b, "interaction.interactionDate") ?? 0) >
                        (_.get(a, "interaction.interactionDate") ?? 0) ||
                    isNullableType(_.get(a, "interaction.interactionDate"))
                ) {
                    return 1;
                }
                return 0;
            });
            this.agentEvaluations = res;
            await this.loadEvaluationModuleScores(orgId, this.agentEvaluations);

            agentDashboardDataGridTabs.forEach((key) => {
                this.dgStoreMap[key].isLoading = false;
            });
        });
    }

    @action
    private async loadEvaluationModuleScores(
        orgId: string,
        evals: AgentCoachingService.AgentEvaluations,
    ) {
        const res =
            await this.agentCoachingService.getModuleScoreForEvaluations(
                evals.all.map((val) => val.id),
            );

        runInAction(() => {
            // map module scores on to evals
            evals.all = evals.all.map((currentEval) => {
                return matchScoreToEval(currentEval, res);
            });
            evals.notCoached = evals.notCoached.map((currentEval) => {
                return matchScoreToEval(currentEval, res);
            });
            agentDashboardTabNames.forEach((tabName) => {
                switch (tabName) {
                    case "ReadyToCoach":
                        this.dgStoreMap[tabName].rows =
                            this.agentEvaluations?.notCoached ?? [];
                        this.dgStoreMap[tabName].removeAllSelectedRows();
                        break;
                    case "Evaluations":
                        this.dgStoreMap[tabName].rows =
                            this.agentEvaluations?.all ?? [];
                        break;
                    case "CompletedCoaching":
                        this.dgStoreMap[tabName].rows =
                            this.agentEvaluations?.coached ?? [];
                        break;
                }
            });
            this.agentEvaluations = { ...evals };
        });
        return res;
    }

    @action
    async loadMyDrafts(agentId: string) {
        const myDraftDgStore = this.dgStoreMap.MyDrafts;
        myDraftDgStore.isLoading = true;
        this.agentDrafts = await this.agentCoachingService.getMyDrafts(agentId);
        myDraftDgStore.isLoading = false;

        // Ready To Coach rows only report that a draft exists. Re-render them
        // after loading this user's drafts so the button can distinguish an
        // owned draft from another coach's draft.
        const readyToCoachStore = this.dgStoreMap.ReadyToCoach;
        readyToCoachStore.setRows([...readyToCoachStore.rows]);

        myDraftDgStore.removeAllSelectedRows();

        // need to do this because agentCoaching relies on the methods inside the AgentCoaching class
        myDraftDgStore.rows = this.agentDrafts
            .filter((ac) => this.isDraftInDateRange(ac))
            .map((draft) => Object.assign(new AgentCoaching(), draft));
    }

    /**
     * The grid shows a draft based on its own Last Modified date — the same value the
     * column displays — and nothing about the evaluations it targets. `agentDrafts`
     * stays unfiltered, since ownership checks are not scoped to the date range.
     */
    private isDraftInDateRange(draft: AgentCoaching) {
        const lastModified = draft.modifiedOn ?? draft.createdOn;
        if (!lastModified) return true;

        const { beginDate, endDate } = this.agentCoachingStore.datePickerStore;

        // Whole days on both ends — the picker's own moments carry a time of day that
        // varies by preset, and the range the evaluations load with is day-aligned too.
        return parseFromISO(lastModified).isBetween(
            beginDate.clone().startOf("day"),
            endDate.clone().endOf("day"),
            undefined,
            "[]",
        );
    }

    async loadEvaluationsAndFormat(
        orgId: string,
        agentId: string,
        startDate: Moment,
        endDate: Moment,
        dateReference?: DateReferenceOption,
        hierarchyIds?: string[],
    ) {
        this.setupAsyncTask("Load Evaluations", async () => {
            if (!agentId || !startDate || !endDate || !orgId) {
                return;
            }

            this.loadAgentMetrics(
                startDate,
                endDate,
                dateReference!,
                this.agentCoachingStore.showMachineEvals,
                agentId,
            );

            await this.loadAgentEvaluations(
                orgId,
                agentId,
                startDate,
                endDate,
                dateReference,
                hierarchyIds,
            );
        });
    }

    /**
     * The conversations this coaching targets directly (coaching with no evaluation).
     */
    getTargetedConversationIds(agentCoaching: AgentCoaching): string[] {
        return targetedConversationIds(agentCoaching);
    }

    currentUserOwnsDraftForEvaluation(
        coachingData: AgentCoachingService.AgentCoachingData,
    ): boolean {
        const currentUserId = this.authStore._user.profile.sub;
        if (coachingData.coachId === currentUserId) return true;

        return this.agentDrafts.some((draft) =>
            (draft.targets ?? []).some(
                (target) =>
                    target.evaluationId === coachingData.id ||
                    (!!coachingData.amdId &&
                        target.audioMetadataId === coachingData.amdId),
            ),
        );
    }

    isReadyToCoachRowSelectable(
        coachingData: AgentCoachingService.AgentCoachingData,
    ) {
        return !(
            coachingData.coachingStatus === AgentCoachingStatus.InProgress &&
            !this.currentUserOwnsDraftForEvaluation(coachingData)
        );
    }

    getReadyToCoachRoute(coachingData: AgentCoachingService.AgentCoachingData) {
        const isLiveEvaluation =
            coachingData.interactionType === InteractionType.Live;
        if (isLiveEvaluation) {
            return EvaluationReviewRoute;
        }

        if (!coachingData.amdId) return undefined;

        return (
            Routes.makeConversationsRoute(coachingData.amdId) +
            "/" +
            Routes.EVAL_COACHING
        );
    }

    /**
     * True when a coaching has to be reviewed on the standalone evaluation review
     * page rather than inside a conversation. Coaching several targets at once has
     * no single conversation to host it — the same reason the Coach button sends a
     * multi-row selection there.
     */
    draftNeedsEvaluationReviewPage(agentCoaching: AgentCoaching): boolean {
        if ((agentCoaching.targets ?? []).length > 1) return true;

        return (
            this.getDraftInteractionType(agentCoaching) === InteractionType.Live
        );
    }

    /**
     * A draft carries no interaction type of its own, so read it off the target the
     * MyDrafts payload hydrated.
     */
    private getDraftInteractionType(
        agentCoaching: AgentCoaching,
    ): InteractionType | undefined {
        return (agentCoaching.targets ?? []).find(
            (target) => target.interactionType !== undefined,
        )?.interactionType;
    }

    /**
     * Where the Edit button on a draft should navigate. Mirrors
     * getReadyToCoachRoute, but keyed on the targets the draft already holds
     * instead of on a row selection.
     */
    getDraftEditRoute(agentCoaching: AgentCoaching): string | undefined {
        if (this.draftNeedsEvaluationReviewPage(agentCoaching)) {
            return EvaluationReviewRoute;
        }

        // A conversation target carries the id directly; an evaluation target has the
        // conversation behind it hydrated onto the target.
        const amdId =
            this.getTargetedConversationIds(agentCoaching)[0] ??
            (agentCoaching.targets ?? []).find(
                (target) => target.evaluationAudioMetadataId,
            )?.evaluationAudioMetadataId;
        if (!amdId) return undefined;

        return (
            Routes.makeConversationsRoute(amdId) + "/" + Routes.EVAL_COACHING
        );
    }

    @action
    reviewDraft(agentCoaching: AgentCoaching) {
        // The eval dataset only covers the selected date range, so a targeted
        // evaluation may not be in it. Review only needs the id and the number, both
        // of which the draft's own target carries.
        const evaluations = (agentCoaching.targets ?? [])
            .filter((target) => !!target.evaluationId)
            .map(
                (target) =>
                    this.agentEvaluations?.all.find(
                        (evaluation) => evaluation.id === target.evaluationId,
                    ) ?? {
                        id: target.evaluationId!,
                        evaluationQbId: target.evaluationQbId,
                    },
            );

        // Conversation-only targets have no evaluation row to resolve, so the
        // targets themselves are what tells us the draft is reviewable.
        if (
            evaluations.length === 0 &&
            (agentCoaching.targets ?? []).length === 0
        ) {
            console.error("Unable to start review process");
            return;
        }

        const evalStore = RootStore().getStore(EvalReviewStore);
        evalStore.initializeStore(
            agentCoaching.agentId,
            evaluations,
            false,
            agentCoaching,
        );
    }

    @action
    reviewSingleEvaluation(evalId: string, isCoached?: boolean) {
        let relevantRecs: AgentCoachingService.AgentCoachingData[] | undefined;
        let toBeReviewed: AgentCoachingService.AgentCoachingData | undefined;
        if (isCoached) {
            relevantRecs = this.dgStoreMap.CompletedCoaching.rows.map((row) => {
                const newCoachData = Object.assign(new AgentCoaching(), row);
                return newCoachData;
            });
        } else {
            relevantRecs = this.dgStoreMap.ReadyToCoach.rows.map((row) => {
                const newCoachData = Object.assign(new AgentCoaching(), row);
                return newCoachData;
            });
        }
        toBeReviewed = relevantRecs?.find((value) => value.id === evalId);
        if (!toBeReviewed || !toBeReviewed?.agentId) {
            console.error("Unable to start review process");
            return;
        }

        RootStore()
            .getStore(EvalReviewStore)
            .initializeStore(
                toBeReviewed?.agentId,
                isCoached
                    ? expandCompletedCoachingReviewTargets(
                          toBeReviewed,
                          this.agentEvaluations,
                      )
                    : [toBeReviewed],
                isCoached || false,
            );
    }

    @action
    private markEvaluationCoachingInProgress(
        evaluationId: string,
        coaching: AgentCoaching,
    ) {
        const updateRow = (
            row: AgentCoachingService.AgentCoachingData,
        ): AgentCoachingService.AgentCoachingData =>
            row.id === evaluationId
                ? {
                      ...row,
                      agentCoachingId: coaching.id ?? coaching.agentCoachingId,
                      coachId: coaching.coachId,
                      coachingStatus: AgentCoachingStatus.InProgress,
                  }
                : row;

        const readyToCoachStore = this.dgStoreMap.ReadyToCoach;
        readyToCoachStore.setRows(
            readyToCoachStore.rows.map((row) =>
                updateRow(row as AgentCoachingService.AgentCoachingData),
            ),
        );

        if (this.agentEvaluations) {
            this.agentEvaluations = {
                ...this.agentEvaluations,
                all: this.agentEvaluations.all.map(updateRow),
                notCoached: this.agentEvaluations.notCoached.map(updateRow),
            };
        }
    }

    @action
    async acquireCoachingLockFromDashboard(
        coachingData: AgentCoachingService.AgentCoachingData,
        targetRoute: string,
    ): Promise<boolean> {
        const agentId = coachingData.agentId;
        const evalReviewStore = this.rootStore.getStore(EvalReviewStore);
        const messageStore = this.rootStore.getStore(MessageStore);

        if (!agentId) {
            messageStore.logError(
                "Unable to identify the coaching target. Please try again.",
            );
            return false;
        }

        if (evalReviewStore.coachingLockStatus !== CoachingLockStatus.Idle) {
            await evalReviewStore.releaseCoachingLock();
        }

        evalReviewStore.reset();
        if (coachingData.interactionType === InteractionType.Live) {
            let currentCoaching: AgentCoaching | undefined;
            try {
                const coachingRecords =
                    await this.agentCoachingService.getCoachingByEvaluation(
                        coachingData.id,
                    );
                currentCoaching =
                    coachingRecords.find(
                        (coaching) =>
                            coaching.coachingStatus ===
                            AgentCoachingStatus.InProgress,
                    ) ?? coachingRecords[0];
            } catch {
                messageStore.logError(
                    "Unable to refresh the coaching status. Please try again.",
                );
                return false;
            }

            if (
                currentCoaching?.coachingStatus ===
                    AgentCoachingStatus.InProgress &&
                currentCoaching.coachId !== this.authStore._user.profile.sub
            ) {
                this.markEvaluationCoachingInProgress(
                    coachingData.id,
                    currentCoaching,
                );
                messageStore.logError(
                    "Another user is currently coaching this. Please try again later.",
                );
                return false;
            }

            const hydratedCoaching = currentCoaching
                ? Object.assign(new AgentCoaching(), currentCoaching)
                : undefined;
            const isCompleted =
                hydratedCoaching?.coachingStatus ===
                AgentCoachingStatus.Completed;

            void evalReviewStore.initializeStore(
                agentId,
                [coachingData],
                isCompleted,
                hydratedCoaching,
            );

            // A stale Ready To Coach row may already be completed. Open it
            // read-only without acquiring an editing lock.
            if (isCompleted) return true;
        } else {
            await evalReviewStore.initializeStoreFromConvos(agentId, [
                coachingData,
            ]);
        }

        const acquired = await evalReviewStore.acquireCoachingLock();
        if (!acquired) {
            if (evalReviewStore.claimCoachingLockFailureNotification()) {
                messageStore.logError(
                    evalReviewStore.coachingLockFailureMessage,
                );
            }
            return false;
        }

        const prepared =
            evalReviewStore.prepareCoachingLockNavigation(targetRoute);
        if (!prepared) await evalReviewStore.releaseCoachingLock();

        return prepared;
    }

    @action
    async coachSelectedEvaluations(navigate: NavigateFunction) {
        return this.setupAsyncTask(CoachSelectedEvaluationsTask, async () => {
            const selectedRowIds = new Set(
                this.dgStoreMap.ReadyToCoach.selectedRowIds.map((rowId) =>
                    rowId.toString(),
                ),
            );
            const selectedRows = this.dgStoreMap.ReadyToCoach.rows.filter(
                (row) => selectedRowIds.has(row.id.toString()),
            ) as AgentCoachingService.AgentCoachingData[];
            const firstSelection = selectedRows[0];
            const evalReviewStore = this.rootStore.getStore(EvalReviewStore);
            const messageStore = this.rootStore.getStore(MessageStore);

            if (!firstSelection?.agentId || selectedRows.length === 0) {
                messageStore.logError(
                    "Please select at least one evaluation to coach.",
                );
                return false;
            }

            if (
                evalReviewStore.coachingLockStatus !== CoachingLockStatus.Idle
            ) {
                await evalReviewStore.releaseCoachingLock();
            }

            evalReviewStore.reset();

            if (selectedRows.length > 1) {
                await evalReviewStore.initializeStore(
                    firstSelection.agentId,
                    selectedRows,
                    false,
                );

                const acquired = await evalReviewStore.acquireCoachingLock();
                if (!acquired) {
                    if (
                        evalReviewStore.claimCoachingLockFailureNotification()
                    ) {
                        messageStore.logError(
                            evalReviewStore.coachingLockFailureMessage,
                        );
                    }
                    return false;
                }

                const prepared = evalReviewStore.prepareCoachingLockNavigation(
                    EvaluationReviewRoute,
                );
                if (!prepared) {
                    await evalReviewStore.releaseCoachingLock();
                    return false;
                }

                navigate(EvaluationReviewRoute);
                return true;
            }

            const targetRoute = this.getReadyToCoachRoute(firstSelection);
            if (!targetRoute) {
                messageStore.logError(
                    "Unable to identify the coaching target. Please try again.",
                );
                return false;
            }

            const acquired = await this.acquireCoachingLockFromDashboard(
                firstSelection,
                targetRoute,
            );
            if (acquired) navigate(targetRoute);

            return acquired;
        });
    }

    @action
    public async deleteSelectedDrafts() {
        const ids = this.dgStoreMap.MyDrafts.selectedRowIds.map((i) =>
            i.toString(),
        );
        if (!ids || ids.length === 0) return;

        this.confirmDeleteIsOpen = false;

        return this.setupAsyncTask("Delete Drafts", async () => {
            try {
                await this.agentCoachingService.deleteDrafts(ids);

                if (this.agentId) {
                    await this.loadMyDrafts(this.agentId);
                    this.dgStoreMap.MyDrafts.removeAllSelectedRows();
                }
            } catch (error: any) {}
        });
    }

    canUserViewTab(tabName: keyof typeof AgentDashboardTabs) {
        const restrictedTabsForAgentUsers: typeof agentDashboardTabNames = [
            "ReadyToCoach",
            "MyDrafts",
        ];

        if (
            this.authStore.isLoggedInUserAgent() &&
            restrictedTabsForAgentUsers.includes(tabName)
        ) {
            return false;
        }

        if (
            tabName === "Dashboard" &&
            !this.authStore.canUserView("Module Scores")
        ) {
            return false;
        }

        // all perms see evaluations tab
        const restrictedTabsForEdit: typeof agentDashboardTabNames = [
            "ReadyToCoach",
            "MyDrafts",
        ];

        const restrictedTabsForView: typeof agentDashboardTabNames = [
            "CompletedCoaching",
            "ReadyToCoach",
            "MyDrafts",
        ];

        const shouldRemoveTab =
            (!this.authStore.canUserEdit("Agent Coaching") &&
                restrictedTabsForEdit.includes(tabName)) ||
            (!this.authStore.canUserView("Agent Coaching") &&
                !this.authStore.isLoggedInUserAgent() &&
                restrictedTabsForView.includes(tabName));

        if (shouldRemoveTab) {
            return false;
        } else {
            return true;
        }
    }

    private async loadAgentMetrics(
        startDate: Moment,
        endDate: Moment,
        dateReference: DateReferenceOption,
        showMachineEvals?: boolean,
        agentId?: string,
    ) {
        const { beginDate: start, endDate: end } = toIsoByDateReference(
            startDate,
            endDate,
            dateReference,
        );

        const res = await this.agentCoachingService.getAgents(
            start!,
            end!,
            dateReference,
            showMachineEvals,
            agentId,
        );

        runInAction(() => {
            this.coachingDetails = res.details;
        });
    }
}
